import { Select } from '@panwds/react-ui';
import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useFilters } from '../../hooks/index.js';
import { Pill } from '../Pill/index.js';
import { PillValue } from '../Pill/PillValue.js';
const emptyItems = [];
/**
 * Provides a Select that will set the filter state for the name given
 * to this component.
 *
 * **Note**: This select is different from the SelectFilter in that it cannot be controlled
 * and expects _all_ of the items to be passed to it. It should be more performant with large
 * lists of items compared to SelectFilter.
 *
 * ### Import Guide
 *
 * ```jsx
 * import { StaticSelectFilter } from "@panwds/react-filters";
 * ```
 */
export function StaticSelectFilter({ autoFocus, name, items = emptyItems, label, onClose, disabled = false, id = 'static-select-filter', ...rest }) {
    const intl = useIntl();
    const { filters, setFilters } = useFilters();
    const handleOnChange = useCallback((changes) => {
        if (rest.enableMultiSelect) {
            setFilters({
                [name]: changes.selectedItems.map((item) => item.value)
            });
        }
        else {
            setFilters({
                [name]: `${changes.selectedItem.value}`
            });
        }
    }, [items, filters, setFilters, rest.enableMultiSelect]);
    const selectedItem = useMemo(() => {
        if (rest.enableMultiSelect) {
            return items.filter((item) => {
                const values = filters[name];
                return Array.isArray(values) && values.includes(item.value);
            });
        }
        return items.find((item) => {
            return item.value === filters[name];
        });
    }, [items, filters[name], rest.enableMultiSelect]);
    const selectableItemsCount = useMemo(() => (rest.enableSelectAll ? items.filter((item) => !item.disabled).length : null), [rest.enableSelectAll, items]);
    const displayLabel = useMemo(() => {
        const noItemSelected = !selectedItem || (Array.isArray(selectedItem) && !selectedItem?.length);
        if (noItemSelected) {
            return intl.formatMessage({
                defaultMessage: 'Select {label}',
                id: 'wU58a8',
                description: 'Label on select when nothing has been selected yet'
            }, {
                label: label
            });
        }
        if (rest.enableSelectAll && Array.isArray(selectedItem) && selectedItem.length === selectableItemsCount) {
            return intl.formatMessage({
                defaultMessage: 'All',
                id: '0jcnlJ',
                description: 'Label on select showing all items have been selected'
            });
        }
        if (Array.isArray(selectedItem)) {
            return selectedItem.map((item) => String(item.children || item.value)).join(', ');
        }
        return selectedItem?.children ?? selectedItem?.value;
    }, [rest.enableSelectAll, selectedItem, selectableItemsCount]);
    const select = (React.createElement(Select, { ...rest, disabled: disabled, items: items, button: React.createElement(PillValue, { labelKey: displayLabel, labelValue: label, disabled: disabled, autoFocus: autoFocus }), onChange: handleOnChange, selectedItem: selectedItem }));
    return (React.createElement("div", { id: id },
        ' ',
        React.createElement(Pill, { label: label, onClose: onClose, select: select }),
        ' '));
}
//# sourceMappingURL=StaticSelectFilter.js.map